import React, {ChangeEvent, ComponentPropsWithoutRef, HTMLAttributes} from 'react';
import styled from 'styled-components';
import {interceptProps} from '@/styled-components/utils';
import {palette} from '@/styles';
import {Typography, typographyStyle} from '../Typography';

type LabelPositionType = 'overlay-top' | 'default';

interface IForm extends ComponentPropsWithoutRef<'textarea'> {
  fullWidth?: boolean;
  label?: string;
  error?: string | boolean;
  success?: string;
  info?: string;
  showCounter?: boolean;
  countHint?: string;
  labelOptions?: {
    position?: LabelPositionType;
  };
  height?: string;
  containerProps?: HTMLAttributes<HTMLDivElement>;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, IForm>(
  (
    {
      height,
      label,
      labelOptions,
      error,
      info,
      success,
      fullWidth,
      value: defaultValue,
      showCounter,
      countHint,
      required,
      containerProps,
      maxLength,
      onBlur,
      onFocus,
      onChange,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = React.useState<IForm['value']>(
      defaultValue || ''
    );
    const [focused, setFocus] = React.useState<boolean>(false);

    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
      onChange?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocus(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setFocus(false);
      onBlur?.(e);
    };

    React.useEffect(() => {
      if (value !== defaultValue) {
        setValue(defaultValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue]);

    const hasValue = !!value;
    const hasCountHint = !!countHint?.length;
    const hasErrorMessage = typeof error === 'string' && !!error?.length;

    return (
      <Root
        labelPosition={labelOptions?.position}
        fullWidth={fullWidth}
        {...containerProps}
      >
        <InputWrapper>
          {label?.length && (
            <Label
              variant="medium12"
              color={palette.gray600}
              float={labelOptions?.position === 'overlay-top'}
            >
              {label}
              {required && <Typography color={palette.red100}>*</Typography>}
            </Label>
          )}
          <InputContainer
            height={height}
            focused={focused}
            value={value}
            error={hasErrorMessage || !!error}
            hasValue={hasValue}
            disabled={props.disabled}
            readOnly={props.readOnly}
          >
            <InputForm
              {...props}
              ref={ref}
              required={required}
              defaultValue={value}
              maxLength={maxLength}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </InputContainer>
        </InputWrapper>
        {(hasCountHint || showCounter) && typeof value === 'string' && (
          <InfoText>
            <Counter>
              {hasCountHint ? (
                <span>{countHint}&nbsp;</span>
              ) : (
                !!maxLength && <span>최대 {maxLength}글자 </span>
              )}
              {value?.length ?? 0}
              {!!maxLength && <span>/{maxLength}</span>}
            </Counter>
          </InfoText>
        )}
        {hasErrorMessage && <InfoText color={palette.red100}>{error}</InfoText>}
        {!!success?.length && (
          <InfoText color={palette.blue100}>{success}</InfoText>
        )}
        {!!info?.length && (
          <InfoText color={palette.textPrimary}>{info}</InfoText>
        )}
      </Root>
    );
  }
);

const Label = styled(Typography)
  .withConfig({
    shouldForwardProp: interceptProps(['float']),
  })
  .attrs({
    as: 'label',
    variant: 'medium12',
  })<{
  float?: boolean;
}>`
  display: block;
  ${({float}) =>
    float
      ? `
    position: absolute;
    pointer-events: none;
    top: -8px;
    left: 7px;
    z-index: 1;
    padding: 0 1px;
    background-color: ${palette.white};
  `
      : `
      margin-bottom: 8px;`}
`;

const InputContainer = styled('div').withConfig({
  shouldForwardProp: interceptProps([
    'error',
    'hasValue',
    'focused',
    'readOnly',
    'disabled',
    'height',
  ]),
})<{
  focused?: boolean;
  hasValue?: boolean;
  error?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  height?: string;
  value?: IForm['value'];
}>`
  position: relative;
  display: flex;
  background-color: ${palette.white};
  border-radius: 8px;
  border: solid 1px ${palette.gray300};
  color: ${palette.textPrimary};
  ${({error, hasValue, focused}) =>
    error
      ? `
  border-color: ${palette.red100};
`
      : focused || hasValue
      ? `
border-color: ${palette.blue100};
${focused ? 'box-shadow: 0px 0px 4px 0px #4A81D5;' : ''}
`
      : `
:hover {
  border-color: ${palette.gray400};
}
`}
  ${({disabled, readOnly}) =>
    (disabled || readOnly) &&
    `background-color: #eee; border-color: #eee !important;`}
  ${({height}) => `height: ${height || '100%'};`}
`;

const InputForm = styled('textarea')`
  box-sizing: border-box;
  resize: none;
  ${{...typographyStyle.medium12}}
  width: 100%;
  height: 100%;
  flex-grow: 1;
  border: none;
  padding: 15px 15px 0;
  background-color: transparent;
  &&& {
    :disabled {
      cursor: default;
      color: ${palette.disabled};
      ::placeholder {
        color: ${palette.disabled};
      }
    }
  }
  :enabled:not([readonly]):focus {
    outline: 0;
  }
  ::placeholder {
    color: ${palette.placeholder};
    transition: all 400ms;
  }
  -webkit-autofill {
    color: #fff !important;
  }
  -webkit-appearance: none;
`;

const Root = styled('div').withConfig({
  shouldForwardProp: interceptProps(['fullWidth', 'labelPosition']),
})<{
  fullWidth?: boolean;
  labelPosition?: LabelPositionType;
}>`
  width: 100%;
  ${({fullWidth}) => !fullWidth && `max-width: 436px;`}
  ${({labelPosition}) => labelPosition === 'overlay-top' && `padding-top: 8px;`}
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Text = styled(Typography).attrs({
  variant: 'medium12',
  as: 'div',
})``;

const InfoText = styled(Text)`
  margin-top: 4px;
`;

const Counter = styled(Text)`
  text-align: right;
  color: #73797e;
`;
